import { useCallback, useEffect, useRef, useState } from 'react';
import { analyticsService, EventSource } from '../utils/analyticsService';

export interface PostViewEventProps {
    containerRef: React.RefObject<HTMLElement | null>;
    djRequestId?: string;
    logContext?: string;
    postId: string;
    collectionId?: string;
    mediaId: string;
    eventSource: EventSource;
    postNumber: number;
    products?: string[];
    isBoosted?: boolean;
    columns?: number;
    disable?: boolean;
}

const SEND_VIEW_TIMOUT = 2000;
const GRID_VIEW_2_SEC_MIN_INTERSECTION_RATIO = 0.5;

export const usePostViewEvent = (props: PostViewEventProps) => {
    const { containerRef, disable, ...logParams } = props;
    const [isSended, setIsSended] = useState(false);

    const postViewEventTimer = useRef<number | undefined>(undefined);

    const sendPostViewEvent = useCallback(() => {
        if (isSended || disable) {
            return;
        }

        setIsSended(true);

        clearTimeout(postViewEventTimer.current);

        analyticsService.trackPostVisible2Sec(logParams);
    }, [disable, isSended, logParams]);

    useEffect(() => {
        const elem = containerRef.current;

        if (elem === null || disable) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.intersectionRatio >= GRID_VIEW_2_SEC_MIN_INTERSECTION_RATIO) {
                        clearTimeout(postViewEventTimer.current);
                        postViewEventTimer.current = window.setTimeout(() => {
                            sendPostViewEvent();
                        }, SEND_VIEW_TIMOUT);
                    } else {
                        clearTimeout(postViewEventTimer.current);
                    }
                });
            },
            { threshold: [0, GRID_VIEW_2_SEC_MIN_INTERSECTION_RATIO, 1] },
        );

        observer.observe(elem);

        return () => {
            if (elem) {
                observer.unobserve(elem);
            }
            clearTimeout(postViewEventTimer.current);
        };
    }, [disable, containerRef, sendPostViewEvent]);

    return { sendPostViewEvent };
};
